import { links, platformDestination } from "../content/links";
import { useI18n } from "../i18n/I18nProvider";
import {
  IconArrow,
  IconBrochure,
  IconLocation,
  IconPlay,
  IconPlatform,
  IconWhatsapp,
} from "./icons";

type Variant = "gradient" | "outline" | "solid";

/**
 * Media column from frames 5:2 and 5:150, matching the reference build: 530px
 * wide, 14px gaps, 310px main photo, two 257x150 thumbnails, a 56px CTA row and
 * an 84px WhatsApp card. The three CTA skins are the ones in the file: the video
 * button is the magenta to purple gradient, the brochure button is white with a
 * purple border, and the last two are solid #8259fe. Icon sits right of the
 * label as in the design, and the four buttons share the full row width.
 */
export function ProjectMedia() {
  const { copy, direction, locale } = useI18n();
  const t = copy.media;

  const ctas = [
    {
      key: "video",
      label: t.video,
      size: "13px",
      icon: <IconPlay />,
      variant: "gradient" as Variant,
      dest: links.video,
    },
    {
      key: "brochure",
      label: t.brochure,
      size: "13px",
      icon: <IconBrochure />,
      variant: "outline" as Variant,
      dest: links.brochure,
    },
    {
      key: "platform",
      label: t.platform,
      size: "12px",
      icon: <IconPlatform />,
      variant: "solid" as Variant,
      dest: platformDestination(locale),
    },
    {
      key: "map",
      label: t.location,
      size: "12px",
      icon: <IconLocation />,
      variant: "solid" as Variant,
      dest: links.map,
    },
  ];

  return (
    <div className="flex w-full flex-col gap-[14px]">
      <h2 className="sr-only text-[19px] font-bold leading-[31px] text-white lg:not-sr-only lg:text-[22px] lg:leading-[41px]">
        {t.title}
      </h2>
      <div dir="ltr" className="flex flex-col gap-[14px]">
        <img
          src="/media/lead-main.webp"
          alt={t.title}
          width={530}
          height={310}
          className="h-[200px] w-full rounded-2xl object-cover lg:h-[310px] lg:rounded-[18px]"
        />
        <div className="grid grid-cols-2 gap-4">
          <img
            src="/media/lead-thumb-1.webp"
            alt=""
            width={257}
            height={150}
            className="h-[108px] w-full rounded-2xl object-cover lg:h-[150px]"
          />
          <img
            src="/media/lead-thumb-2.webp"
            alt=""
            width={257}
            height={150}
            className="h-[108px] w-full rounded-2xl object-cover lg:h-[150px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 xl:flex xl:gap-2">
          {ctas.map((cta) => (
            <CtaButton
              key={cta.key}
              label={cta.label}
              size={cta.size}
              icon={cta.icon}
              variant={cta.variant}
              href={cta.dest.enabled ? cta.dest.href : null}
              direction={direction}
            />
          ))}
        </div>
        <WhatsAppCard />
      </div>
    </div>
  );
}

function CtaButton({
  label,
  size,
  icon,
  variant,
  href,
  direction,
}: {
  label: string;
  size: string;
  icon: React.ReactNode;
  variant: Variant;
  href: string | null;
  direction: "rtl" | "ltr";
}) {
  const base =
    "flex h-[52px] min-w-0 flex-1 items-center justify-center gap-[5px] rounded-xl px-1.5 font-bold leading-[24.4px] lg:h-14";
  const skin: Record<Variant, string> = {
    gradient: "gradient-cta text-white",
    outline: "border border-purple bg-white text-purple",
    solid: "bg-[#8259fe] text-white",
  };

  const content = (
    <>
      <span
        dir={direction}
        style={{ fontSize: size }}
        className="min-w-0 truncate whitespace-nowrap"
      >
        {label}
      </span>
      <span className="shrink-0 text-[18px] leading-none lg:text-[20px]">{icon}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={`${base} ${skin[variant]}`}>
        {content}
      </a>
    );
  }
  return (
    <span aria-disabled="true" className={`${base} ${skin[variant]} cursor-not-allowed opacity-60`}>
      {content}
    </span>
  );
}

function WhatsAppCard() {
  const { copy, direction } = useI18n();
  const t = copy.media;
  const href = links.whatsapp.enabled ? links.whatsapp.href : null;

  const inner = (
    <div dir="ltr" className="flex w-full items-center gap-[10px]">
      <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-tint-3 text-[16px] text-[#7b55e8]">
        <IconArrow />
      </span>
      <div dir={direction} className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[14px] font-bold leading-[22px] text-ink">
          {t.whatsappTitle}
        </span>
        <span className="truncate text-[11px] leading-[18px] text-body">{t.whatsappSubtitle}</span>
      </div>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[22px] bg-whatsapp text-[29px] text-white">
        <IconWhatsapp />
      </span>
    </div>
  );

  const shared =
    "flex items-center rounded-[15px] border border-tint-line bg-tint-2 px-[14px] py-3 shadow-whatsapp";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={copy.a11y.whatsapp}
        className={shared}
      >
        {inner}
      </a>
    );
  }
  return (
    <div aria-disabled="true" className={shared}>
      {inner}
    </div>
  );
}
